// Inline validation for numeric inputs (Antenore 2026-06-08: "ci sono dei
// controlli sull'input?"). Returns null when the field is empty or valid;
// 'invalid' for a malformed number; 'range' for a value outside the field's
// allowed range. Locale-aware: a comma decimal ("8,5") is accepted on
// IT/FR/DE/RU, the same normalisation parseLocaleNumber does at save — so the
// system separator never reads as an error. Forgiving mid-typing: a trailing
// dot ("8.") is accepted so the field doesn't flash between keystrokes.
//
// Lifted from apps/mobile/src/logger (P3 of the scheduler-composer plan) so
// the shared composer's metric cells validate with the same rules as both
// loggers. Pure — keep free of React / platform imports.
import { parseLocaleNumber } from "./parseLocaleNumber.js";

export type NumericFieldKind =
  | "reps"
  | "weight"
  | "rir"
  | "rpe"
  // Composer prescription reps: accepts the AMRAP suffix ("5+" = at
  // least 5). The loggers' performed-reps fields stay plain "reps" — a
  // logged set has an exact count.
  | "prescribedReps"
  // Whole seconds — the HIIT work/rest cells (HI-1). Integer only and
  // capped at an hour, matching the schema's int bound, so the field
  // says no at the keystroke instead of at save.
  | "seconds";
export type NumericFieldError = "invalid" | "range";

export function validateNumericField(
  kind: NumericFieldKind,
  raw: string,
): NumericFieldError | null {
  let v = (raw ?? "").trim();
  if (v === "") return null;
  if (kind === "prescribedReps") {
    v = v.replace(/\+$/, "").trim();
    if (v === "") return "invalid"; // a bare "+" prescribes no floor
    kind = "reps";
  }
  // Bodyweight extra carries an optional leading minus (band/machine assist);
  // strip it before the numeric check.
  const body = kind === "weight" ? v.replace(/^[-−]/, "").trim() : v;
  if (body === "") return null;
  const norm = body.replace(/,/g, ".");
  // Digits with at most one decimal dot, and at least one digit present. This
  // catches the genuine errors a numeric keyboard can still produce: a pasted
  // value, or two separators ("1,2,3" → "1.2.3").
  if (!/^\d*\.?\d*$/.test(norm) || !/\d/.test(norm)) return "invalid";
  const n = parseLocaleNumber(norm);
  if (!Number.isFinite(n)) return "invalid";
  if (kind === "seconds") {
    if (!Number.isInteger(n) || n < 0) return "invalid";
    return n <= 3600 ? null : "range";
  }
  if (kind === "reps") return Number.isInteger(n) && n >= 0 ? null : "invalid";
  if (kind === "rir")
    return Number.isInteger(n) && n >= 0 && n <= 10 ? null : "range";
  if (kind === "rpe") return n >= 1 && n <= 10 ? null : "range";
  return n >= 0 ? null : "invalid"; // weight
}

// Localized message for an invalid field, or null when valid. Kept here so
// every numeric surface (card SetRow, timeline FocusPanelInputs, composer
// metric cells) shares one wording source.
export function numericFieldErrorText(
  kind: NumericFieldKind,
  raw: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string | null {
  const code = validateNumericField(kind, raw);
  if (!code) return null;
  if (code === "range" && kind === "rir") {
    return t("mobile.logger.numRange", {
      label: "RIR",
      min: 0,
      max: 10,
      defaultValue: "RIR {{min}}–{{max}}",
    });
  }
  if (code === "range" && kind === "seconds") {
    return t("mobile.logger.numRange", {
      label: t("program.seconds", { defaultValue: "Seconds" }),
      min: 0,
      max: 3600,
      defaultValue: "{{label}} {{min}}–{{max}}",
    });
  }
  if (code === "range" && kind === "rpe") {
    return t("mobile.logger.numRange", {
      label: "RPE",
      min: 1,
      max: 10,
      defaultValue: "RPE {{min}}–{{max}}",
    });
  }
  return t("mobile.logger.numInvalid", { defaultValue: "Invalid number" });
}
